import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import useErrorStore from '@common/common/stores/error';
import type { ErrorStateObj } from '@common/common/types/error';
import {
    getTrendReportPageApiAdmin,
    postTrendReportPageWithFilterApiAdmin,
    getTotalPageApiAdmin,
    postGetTotalPageApiAdmin,
    getTimeseriesDataApiAdmin,
    getModuleDataApiAdmin,
    patchChangeIsPublishApiAdmin,
    patchModuleDataInSheetApiAdmin,
    postRegenerateModuleDataApiAdmin,
    getReportExistingEndDatesApiAdmin,
    patchChangeIsHumanConfirmedApiAdmin,
    patchChangeIsHaveToReviewApiAdmin,
    getModuleDataOnlyPublishedApiAdmin,
} from '@common/trend-report-common';
import type {
    Evaluation,
    ManagingPageContentsInfo,
    ManagingPageMetaInfo,
    TimeSeriesResDto,
    TrendReportManagingPageResDto,
    TrendReportResDto,
} from '@common/openapi-client/openapi';
import type {
    PatchModuleDataInSheetPayload,
    RegenerateModulePayload,
    TrendReportFilterRequestBody,
} from '@common/trend-report-common';
import type { MonthlyTimeseriesData } from '@common/trend-report-common';

// 누락된 타입 정의 추가
type ModuleDataType = TrendReportResDto;
type TimeseriesDataType = TimeSeriesResDto;

/**
 * AIDEV-NOTE: 월별 캐시 엔트리.
 *
 * 이 신원 검증과 아래의 요청 세대 토큰(requestSeq)은 **관리자 스토어에만** 있다.
 * 유저 스토어(proReport.ts)에는 의도적으로 넣지 않았다 — 그쪽 monthlyTimeseriesMap
 * 선언부 주석에 왜 없어도 되는지와 알려진 잔여 이슈를 적어뒀다.
 * 관리자는 한 화면에서 여러 유저의 리포트를 오가는 유일한 경로다.
 *
 * 맵의 키는 endDate 뿐이라 "누구의 / 어떤 종류의" 데이터인지 키만 봐서는 알 수 없다.
 * 그래서 값에 신원(owner)을 같이 담고 read 시점에 검증한다. 신원이 다르면 캐시 미스로
 * 처리해 재조회하므로, 맵을 비우는 주체가 누구든(혹은 안 비우든) 엉뚱한 데이터가
 * 표시되는 일이 구조적으로 불가능해진다.
 * 별도 owner 맵을 두지 않는 이유: 두 구조를 동기화해야 하는 새 약점이 생긴다.
 */
type CachedByOwner<T> = { owner: string; data: T | null };

const useProReportAdminStore = defineStore('proReportAdmin', () => {
    const errorStore = useErrorStore();
    const timeseriesData = ref<TimeSeriesResDto | null>(null);
    const moduleData = ref<TrendReportResDto | null>(null);
    const currentSelectedEvaluation = ref<Evaluation | null>(null);
    // const currentSelectedMonthYear = ref<string | null>(null);
    const moduleDataMap = ref<Record<string, TrendReportResDto>>({}); // 지금, 이전, 다음 월 데이터 캐싱
    const reportExistingEndDates = ref<string[]>([]);
    const monthlyTimeseriesList = ref<
        {
            yearMonthKey: string;
            timeseriesData: TimeSeriesResDto | null;
            moduleData: TrendReportResDto | null;
            endDate: string; // moduleData 호출용
            isError?: boolean; // 네트워크 실패 등
        }[]
    >([]);
    const currentSelectedEndDate = ref<string | null>(null);
    // 테이블에서 클릭한 리포트의 endDate (이 리포트는 항상 module-data API 사용)
    const clickedReportEndDate = ref<string | null>(null);

    // 📌 월별 캐시된 데이터 맵 (키: endDate, 값: 신원 + 데이터. CachedByOwner 주석 참고)
    const monthlyModuleMap = ref<Record<string, CachedByOwner<ModuleDataType>>>({});
    const monthlyTimeseriesMap = ref<Record<string, CachedByOwner<TimeseriesDataType>>>({});

    // AIDEV-NOTE: 캐시 엔트리 신원 키.
    // timeseries API 는 version 을 받지 않으므로 uid + reportType 으로 충분하다.
    // module 은 version 과 조회 API 종류(useOnlyPublished)까지 넣는다. 발행본만 담긴 엔트리를
    // 원본(version 지정) 요청이 재사용하면 클릭한 버전과 다른 내용이 표시되기 때문이다.
    const timeseriesOwnerKey = (userUid: string, reportType: string) => `${userUid}|${reportType}`;
    const moduleOwnerKey = (userUid: string, reportType: string, version: number, useOnlyPublished: boolean) =>
        `${userUid}|${reportType}|${version}|${useOnlyPublished}`;

    const trendReportPageData = ref<TrendReportManagingPageResDto | null>(null);
    const currentPage = ref<number>(1);
    const PAGE_SIZE = 70;
    const pageInfo = ref<ManagingPageMetaInfo>({
        TotalCount: 0,
        TotalPage: 0,
        CurrentPage: 0,
        PageSize: 0,
    });
    const selectedRowData = ref<ManagingPageContentsInfo | null>(null);
    const isLoadingPage = ref<boolean>(false);
    // AIDEV-NOTE: 목록 조회 실패 여부. 예전엔 실패해도 trendReportPageData 가 그대로라
    // 화면이 "검색 결과가 없습니다"만 띄웠고, 운영자가 필터 미스와 요청 실패를 구분할 수 없었다.
    // (실제로 CORS 차단 상황에서 4041건이 있는데도 "결과 없음"으로 보였다)
    const pageLoadError = ref<boolean>(false);

    // AIDEV-NOTE: 요청 세대(generation) 토큰.
    // 목록에서 다른 행을 연달아 클릭하면 이전 행의 비동기 체인이 취소되지 않은 채 살아남아
    // monthlyModuleMap/monthlyTimeseriesMap 에 늦게 write 하고, 새 행이 그 값을 캐시 히트해서
    // "클릭한 행과 다른 내용"이 표시되던 문제가 있었다. (맵 키가 endDate 뿐이라 유저가 달라도 충돌)
    // beginReportSession() 으로 세대를 올리고, fetcher 의 write 지점에서 isStale 로 걸러낸다.
    // ref 가 아니라 평범한 let 인 이유: watchEffect 안에서 읽고 쓰이므로 반응형이면 자기 자신을
    // 재실행시킬 위험이 있고, 화면이 이 값을 구독할 이유도 없다.
    let requestSeq = 0;
    const isStale = (seq: number) => seq !== requestSeq;

    // AIDEV-NOTE: 로딩 스피너는 "행 선택" 단위로만 소유된다. 월 이동은 requestSeq 만 올리고
    // 스피너를 건드리지 않으므로, isStale 로 스피너를 판단하면 행 클릭 체인이 월 이동에 밀렸을 때
    // 스피너를 아무도 못 끄게 된다. 그래서 세션 세대를 따로 둔다.
    let sessionSeq = 0;
    const isStaleSession = (seq: number) => seq !== sessionSeq;

    // AIDEV-NOTE: 목록(페이지) 요청 세대 토큰. 위의 리포트 조회용 requestSeq 와는 별개 관심사다.
    // updateCurrentPage 는 currentPage 를 동기로 바꾸고 조회는 비동기로 하는데 취소 수단이 없었다.
    // 페이지 번호를 연달아 누르면 응답 순서가 뒤바뀌어(체인당 요청 2개라 쉽게 발생) 늦게 도착한
    // 옛 페이지가 trendReportPageData 를 덮어썼고, "3페이지 하이라이트 + 2페이지 목록" 상태가
    // 자기교정 없이 남았다. (40/80ms 간격 재현) 세대를 올려 폐기된 체인의 write 를 막는다.
    let pageSeq = 0;
    const isStalePage = (seq: number) => seq !== pageSeq;
    const beginPageRequest = () => ++pageSeq;

    /**
     * 테이블에서 새 행을 선택했을 때 호출한다.
     * 월 캐시를 비우고 새 세대를 발급해서, 진행 중이던 이전 행의 체인을 무효화한다.
     */
    const beginReportSession = (endDate: string | null) => {
        monthlyTimeseriesMap.value = {};
        monthlyModuleMap.value = {};
        clickedReportEndDate.value = endDate;
        sessionSeq = ++requestSeq;
        return sessionSeq;
    };

    const computedTimeseriesData = computed(() =>
        currentSelectedEndDate.value ? (monthlyTimeseriesMap.value[currentSelectedEndDate.value]?.data ?? null) : null,
    );

    const computedModuleData = computed(() =>
        currentSelectedEndDate.value ? (monthlyModuleMap.value[currentSelectedEndDate.value]?.data ?? null) : null,
    );

    // AIDEV-NOTE: 한 번의 페이지 전환은 getTotalPage + getTrendReportPage 두 요청으로 이뤄지므로
    // 두 단계를 같은 세대로 묶어야 한다. 중간에 새 전환이 시작되면 여기서 끊는다.
    const updateCurrentPage = (page: number, filter?: TrendReportFilterRequestBody) => {
        const seq = beginPageRequest();
        currentPage.value = page;
        isLoadingPage.value = true;
        return getTotalPage(filter, seq).then(() => {
            if (isStalePage(seq)) return;
            return getTrendReportPage(page, filter, seq);
        });
    };

    const initPageData = (filter?: TrendReportFilterRequestBody) => {
        currentSelectedEvaluation.value = null;
        selectedRowData.value = null;
        updateCurrentPage(1, filter);
    };

    const updateSelectedRowData = (row: ManagingPageContentsInfo) => {
        selectedRowData.value = row;
    };

    const updateTargetSelectedRowData = (row: ManagingPageContentsInfo) => {
        selectedRowData.value = row;

        const list = trendReportPageData.value?.ManagingPageContentsInfoList;
        // AIDEV-NOTE: Version/ReportCode 까지 매칭해야 동일 GenerationDate 의 다른 Version row 가 섞일 때
        // 엉뚱한(맨 위 = 최고 Version) row 를 덮어쓰지 않는다. (낮은 Version 발행 시 위로 끌어올려지던 버그)
        const targetIndex = list?.findIndex(
            (item: ManagingPageContentsInfo) =>
                item.Uid === row.Uid &&
                item.EndDate === row.EndDate &&
                item.StartDate === row.StartDate &&
                item.ReportType === row.ReportType &&
                item.GenerationDate === row.GenerationDate &&
                item.Version === row.Version &&
                item.ReportCode === row.ReportCode,
        );

        if (list && targetIndex !== undefined && targetIndex !== -1) {
            list[targetIndex] = row;
        }
    };

    const removeItemFromList = (row: ManagingPageContentsInfo) => {
        const list = trendReportPageData.value?.ManagingPageContentsInfoList;
        if (!list) return;

        // AIDEV-NOTE: updateTargetSelectedRowData 와 동일하게 Version/ReportCode 까지 매칭해야
        // 필터 활성 상태에서 토글 시 엉뚱한(맨 위) row 가 제거되지 않는다.
        const targetIndex = list.findIndex(
            (item: ManagingPageContentsInfo) =>
                item.Uid === row.Uid &&
                item.EndDate === row.EndDate &&
                item.StartDate === row.StartDate &&
                item.ReportType === row.ReportType &&
                item.GenerationDate === row.GenerationDate &&
                item.Version === row.Version &&
                item.ReportCode === row.ReportCode,
        );

        if (targetIndex !== -1) {
            list.splice(targetIndex, 1);
            // 선택된 row가 삭제된 row라면 선택 해제 (Version/ReportCode 까지 일치할 때만)
            if (
                selectedRowData.value?.Uid === row.Uid &&
                selectedRowData.value?.GenerationDate === row.GenerationDate &&
                selectedRowData.value?.Version === row.Version &&
                selectedRowData.value?.ReportCode === row.ReportCode
            ) {
                selectedRowData.value = null;
            }
            // TotalCount 감소
            if ((pageInfo.value.TotalCount ?? 0) > 0) {
                pageInfo.value.TotalCount = (pageInfo.value.TotalCount ?? 0) - 1;
            }
        }
    };

    // 필터 옵션 타입
    interface FilterOptionForCheck {
        IsConfirm: boolean | null;
        IsPublish: boolean | null;
    }

    // 아이템이 필터 조건에 맞는지 체크
    const checkItemMatchesFilter = (item: ManagingPageContentsInfo, filterOption: FilterOptionForCheck): boolean => {
        // IsConfirm 필터 체크
        if (filterOption.IsConfirm !== null && item.IsConfirmed !== filterOption.IsConfirm) {
            return false;
        }

        // IsPublish 필터 체크 (PublishString 기준)
        if (filterOption.IsPublish !== null) {
            const isPublished = item.PublishString !== '발행하기'; // '발행하기'면 아직 미발행
            if (isPublished !== filterOption.IsPublish) {
                return false;
            }
        }

        return true;
    };

    // 응답 데이터 처리 (필터 체크 후 업데이트 또는 제거)
    const handleResponseWithFilter = (data: ManagingPageContentsInfo, filterOption?: FilterOptionForCheck) => {
        // 필터가 실제로 설정되었는지 확인 (IsConfirm 또는 IsPublish가 null이 아닌 경우)
        const hasActiveFilter = filterOption && (filterOption.IsConfirm !== null || filterOption.IsPublish !== null);

        if (hasActiveFilter) {
            if (checkItemMatchesFilter(data, filterOption)) {
                updateTargetSelectedRowData(data);
            } else {
                removeItemFromList(data);
            }
        } else {
            updateTargetSelectedRowData(data);
        }
    };

    const getTotalPage = async (filter?: TrendReportFilterRequestBody, seq?: number) => {
        try {
            const resp = filter
                ? await postGetTotalPageApiAdmin(PAGE_SIZE, filter)
                : await getTotalPageApiAdmin(PAGE_SIZE);
            if (seq !== undefined && isStalePage(seq)) return resp;
            pageInfo.value.TotalPage = resp.Data;
            return resp;
        } catch (error) {
            console.error('Error fetching total page:', error);
        }
    };

    // AIDEV-NOTE: seq 를 넘기지 않으면 진입 시점에 새 세대를 발급한다(단독 호출 호환).
    const getTrendReportPage = async (
        page: number,
        filter?: TrendReportFilterRequestBody,
        seq: number = beginPageRequest(),
    ) => {
        isLoadingPage.value = true;
        try {
            const resp = filter
                ? await postTrendReportPageWithFilterApiAdmin(page, PAGE_SIZE, filter)
                : await getTrendReportPageApiAdmin(page, PAGE_SIZE);
            // 폐기된 체인은 목록/메타를 덮어쓰지 않는다
            if (isStalePage(seq)) return resp;
            pageLoadError.value = false;
            trendReportPageData.value = resp.Data;
            const meta = resp.Data.ManagingPageMetaInfo;
            pageInfo.value.TotalCount = meta?.TotalCount ?? 0;
            pageInfo.value.CurrentPage = meta?.CurrentPage ?? 0;
            pageInfo.value.PageSize = meta?.PageSize ?? 0;
            return resp;
        } catch (error) {
            console.error('Error fetching trend report page:', error);
            // 이전 목록이 남아 있어도 실패는 실패로 알린다. 옛 페이지를 새 페이지인 척
            // 보여주는 것이 이 화면에서 반복돼 온 혼란의 원인이었다.
            if (!isStalePage(seq)) pageLoadError.value = true;
        } finally {
            // 진행 중인 새 체인의 스피너를 끄지 않는다
            if (!isStalePage(seq)) isLoadingPage.value = false;
        }
    };

    // 리포트 조회 가능한 endDate 조회
    const getReportExistingEndDates = async (userUid: string, seq?: number) => {
        try {
            const resp = await getReportExistingEndDatesApiAdmin(userUid);
            // AIDEV-NOTE: 전역 상태라 다른 유저의 목록이 덮어쓰면
            // updateMonthlySurroundingData 의 length/isInRange 분기가 엉뚱하게 갈린다.
            if (seq !== undefined && isStale(seq)) return;
            reportExistingEndDates.value = resp.Data.reverse();
        } catch (error) {
            console.error('Error fetching report existing end dates:', error);
        }
    };

    const getTimeseriesData = async (userUid: string, endDate: string, reportType: string, seq?: number) => {
        const owner = timeseriesOwnerKey(userUid, reportType);
        // AIDEV-NOTE: 신원이 일치할 때만 캐시를 재사용한다. 다르면 미스로 처리해 재조회 후 덮어쓴다.
        // cached.data 진위 검사는 신원 검증 도입 전 동작(`if (map[endDate])`)을 보존하기 위한 것이다.
        // 엔트리 래핑({owner,data})으로 바뀌면서 data 가 null 이어도 래퍼는 truthy 이므로,
        // 이 검사가 없으면 없던 negative caching 이 생겨 null 응답을 재조회하지 않게 된다.
        const cached = monthlyTimeseriesMap.value[endDate];
        if (cached && cached.owner === owner && cached.data) {
            return cached.data;
        }
        try {
            const resp = await getTimeseriesDataApiAdmin(userUid, endDate, reportType);

            // const evaluations = resp.Data?.Evaluation;
            // if (evaluations && evaluations.length > 0) {
            //     const lastValidEvaluation = [...evaluations]
            //         .reverse()
            //         .find(evalItem => evalItem.EvaluationText !== 'lock');
            //     if (lastValidEvaluation) {
            //         updateCurrentSelectedEvaluation(
            //             lastValidEvaluation,
            //             userUid,
            //         );
            //     }
            // }
            // AIDEV-NOTE: 폐기된 세대의 응답은 캐시에 쓰지 않는다. 여기서 막지 않으면
            // 다른 행을 클릭해 맵을 비운 뒤에 옛 응답이 들어와 새 행이 그걸 캐시 히트한다.
            if (seq !== undefined && isStale(seq)) return resp.Data;
            timeseriesData.value = resp.Data;
            monthlyTimeseriesMap.value[endDate] = { owner, data: resp.Data };
            return resp.Data; // ApiResponse 대신 Data만 반환
        } catch (error) {
            console.error('Error fetching timeseries data:', error);
            // 이미 떠난 리포트의 실패로 전역 에러를 띄우지 않는다
            if (seq !== undefined && isStale(seq)) return null;
            const errorData: ErrorStateObj = {
                errorType: 'GLOBAL_ERROR',
                errorMessage: '서버에 일시적인 오류가 발생했습니다. 앱을 다시 실행해주세요.',
            };
            errorStore.setError(errorData);
            timeseriesData.value = null;
            return null; // 에러 시 null 반환
        }
    };

    /**
     * 모듈 데이터 조회 함수
     *
     * 관리자 페이지에서의 API 호출 전략:
     * - useOnlyPublished === false (기본값): 테이블에서 클릭한 리포트의 첫 페이지 호출 시
     *   → 기존 API (getModuleDataApiAdmin) 사용 - version과 reportType 필요
     * - useOnlyPublished === true: 클릭한 리포트 날짜가 아닌 다른 날짜 데이터 호출 시
     *   → 새 API (getModuleDataOnlyPublishedApiAdmin) 사용 - 발행된 데이터만 조회
     */
    const getModuleData = async (
        userUid: string,
        endDate: string,
        version: number,
        reportType: string,
        useOnlyPublished: boolean = false,
        seq?: number,
    ) => {
        // 이미 존재하면 재요청하지 않음

        // if (moduleDataMap.value[endDate]) {
        //     return;
        // }
        const owner = moduleOwnerKey(userUid, reportType, version, useOnlyPublished);
        // AIDEV-NOTE: getTimeseriesData 와 동일 - 신원 일치 + data 진위(negative caching 방지).
        const cached = monthlyModuleMap.value[endDate];
        if (cached && cached.owner === owner && cached.data) {
            return cached.data;
        }
        try {
            let resp;
            if (useOnlyPublished) {
                // 다른 날짜 데이터는 발행된 데이터만 조회
                // GET /backoffice/trend-report/module-data-only-published
                resp = await getModuleDataOnlyPublishedApiAdmin(userUid, endDate);
            } else {
                // 첫 페이지 호출은 기존 API 사용
                // GET /backoffice/trend-report/module-data (version, reportType 필요)
                resp = await getModuleDataApiAdmin(userUid, endDate, version, reportType);
            }
            // AIDEV-NOTE: getTimeseriesData 와 동일 - 폐기된 세대의 응답은 캐시에 쓰지 않는다.
            if (seq !== undefined && isStale(seq)) return resp.Data;
            moduleData.value = resp.Data;
            monthlyModuleMap.value[endDate] = { owner, data: resp.Data };
            return resp.Data; // ApiResponse 대신 Data만 반환
        } catch (error) {
            console.error('Error fetching module data:', error);
            if (seq !== undefined && isStale(seq)) return null;
            moduleData.value = null;
            return null; // 에러 시 null 반환
        }
    };

    const getModuleDataOnlyPublished = async (userUid: string, endDate: string) => {
        try {
            const resp = await getModuleDataOnlyPublishedApiAdmin(userUid, endDate);
            return resp.Data; // ApiResponse 대신 Data만 반환
        } catch (error) {
            console.error('Error fetching module data only published:', error);
            return null; // 에러 시 null 반환
        }
    };

    // const updateCurrentSelectedEvaluation = (
    //     evaluation: Evaluation,
    //     userUid: string,
    // ) => {
    //     currentSelectedEvaluation.value = evaluation;
    //     if (
    //         selectedRowData.value?.Version != null &&
    //         selectedRowData.value?.ReportType
    //     ) {
    //         getModuleData(
    //             userUid,
    //             evaluation.EndDate ?? '',
    //             selectedRowData.value.Version,
    //             selectedRowData.value.ReportType,
    //         );
    //     }
    // };

    const patchChangeIsPublish = async (
        userUid: string,
        reportType: string,
        generationDate: string,
        version: string,
        reportCode: string,
        filterOption?: FilterOptionForCheck,
    ) => {
        try {
            const resp = await patchChangeIsPublishApiAdmin(userUid, reportType, generationDate, version, reportCode);
            handleResponseWithFilter(resp.Data, filterOption);
            return resp;
        } catch (error) {
            console.error('Error patching publish state:', error);
        }
    };

    const patchChangeIsHumanConfirmed = async (
        userUid: string,
        reportType: string,
        generationDate: string,
        version: string,
        reportCode: string,
        filterOption?: FilterOptionForCheck,
    ) => {
        try {
            const resp = await patchChangeIsHumanConfirmedApiAdmin(
                userUid,
                reportType,
                generationDate,
                version,
                reportCode,
            );
            handleResponseWithFilter(resp.Data, filterOption);
            return resp;
        } catch (error) {
            console.error('Error patching human confirmed state:', error);
        }
    };

    const patchChangeIsHaveToReview = async (
        userUid: string,
        reportType: string,
        generationDate: string,
        version: string,
        reportCode: string,
        filterOption?: FilterOptionForCheck,
    ) => {
        try {
            const resp = await patchChangeIsHaveToReviewApiAdmin(
                userUid,
                reportType,
                generationDate,
                version,
                reportCode,
            );
            if (resp.IsSuccess) {
                handleResponseWithFilter(resp.Data, filterOption);
            }
            return resp;
        } catch (error) {
            console.error('Error patching have to review state:', error);
        }
    };

    const postRegenerateModuleData = async (payload: RegenerateModulePayload) => {
        try {
            const resp = await postRegenerateModuleDataApiAdmin(payload);
            setTimeout(() => {
                updateTargetSelectedRowData(resp.Data);
            }, 100);
            return resp;
        } catch (error) {
            console.error('Error regenerating module data:', error);
        }
    };

    const patchModuleDataInSheet = async (payload: PatchModuleDataInSheetPayload) => {
        try {
            const resp = await patchModuleDataInSheetApiAdmin(payload);
            setTimeout(() => {
                updateTargetSelectedRowData(resp.Data);
            }, 100);
            return resp;
        } catch (error) {
            console.error('Error patching module data in sheet:', error);
        }
    };

    // getTimeseriesData 호출 후 getModuleData 호출
    const fetchMonthlyTimeseriesWithModules = async (
        userUid: string,
        baseEndDate: string,
        _version: number,
        reportType: string,
    ) => {
        const getMonthKey = (dateStr: string) => {
            const [year, month] = dateStr.split('T')[0].split('-');
            return `${year}-${month}`;
        };

        const baseDate = new Date(baseEndDate);
        const monthlyOffsets = [0, -1, -2];
        const newResults: MonthlyTimeseriesData[] = [];

        for (const offset of monthlyOffsets) {
            const targetDate = new Date(baseDate);
            targetDate.setMonth(targetDate.getMonth() + offset);

            const yyyy = targetDate.getFullYear();
            const mm = String(targetDate.getMonth() + 1).padStart(2, '0');
            const dummyEndDate = `${yyyy}-${mm}-01T00:00:00`;
            const monthKey = `${yyyy}-${mm}`;

            // 이미 동일 endDate가 존재하면 호출 생략
            const alreadyExists = monthlyTimeseriesList.value.some(item => getMonthKey(item.endDate) === monthKey);
            if (alreadyExists) continue;

            let timeseries: TimeSeriesResDto | null = null;
            let moduleData: TrendReportResDto | null = null;
            let endDate = dummyEndDate;
            let isError = false;

            try {
                const tsResp = await getTimeseriesDataApiAdmin(userUid, dummyEndDate, reportType);
                timeseries = tsResp.Data;
                endDate = tsResp.Data?.Evaluation?.EndDate ?? dummyEndDate;
                monthlyTimeseriesMap.value[endDate] = {
                    owner: timeseriesOwnerKey(userUid, reportType),
                    data: tsResp.Data,
                };
            } catch {
                isError = true;
            }

            try {
                // fetchMonthlyTimeseriesWithModules는 실제로 사용되지 않는 것으로 보이며,
                // 사용되더라도 다른 날짜 데이터이므로 새 API 사용
                const mdResp = await getModuleDataOnlyPublishedApiAdmin(userUid, endDate);
                moduleData = mdResp.Data;
                // useOnlyPublished 로 조회했으므로 신원도 그렇게 태깅한다
                monthlyModuleMap.value[endDate] = {
                    owner: moduleOwnerKey(userUid, reportType, _version, true),
                    data: moduleData,
                };
            } catch {
                isError = true;
            }

            newResults.push({
                yearMonthKey: monthKey,
                timeseriesData: timeseries,
                moduleData,
                endDate,
                isError,
            });

            // 기준 월이면 현재 선택된 값 갱신
            if (offset === 0) {
                currentSelectedEndDate.value = endDate;
                timeseriesData.value = timeseries;
            }
        }

        // 병합 및 중복 제거
        const merged = [...monthlyTimeseriesList.value, ...newResults];
        const uniqueSorted = Array.from(new Map(merged.map(item => [item.endDate, item])).values()).sort(
            (a, b) => parseYMDToDate(a.endDate).getTime() - parseYMDToDate(b.endDate).getTime(),
        );

        monthlyTimeseriesList.value = uniqueSorted;

        // 현재 화면 표시용 모듈 데이터 지정
        const current = uniqueSorted.find(item => item.endDate === currentSelectedEndDate.value);
        moduleData.value = current?.moduleData ?? null;
    };

    // 해당하는 월만 타겟팅해서 timeseriesData와 moduleData 데이터 호출
    const fetchMonthlyTimeseriesWithModulesForSingleMonth = async (
        userUid: string,
        endDateStr: string, // 정확한 말일 포맷 (ex: 2023-08-31T00:00:00)
        version: number,
        reportType: string,
    ) => {
        const getMonthKey = (dateStr: string) => {
            const [year, month] = dateStr.split('T')[0].split('-');
            return `${year}-${new Date(month).getMonth()}`;
        };

        const monthKey = getMonthKey(endDateStr);

        // 이미 존재하면 패스
        // const alreadyExists = monthlyTimeseriesList.value.some(
        //     item => getMonthKey(item.endDate) === monthKey,
        // );
        // if (alreadyExists) return;

        let timeSeriesRespData: TimeSeriesResDto | null = null;
        let moduleRespData: TrendReportResDto | null = null;
        let isError = false;

        try {
            // const tsResp = await getTimeseriesDataApi(
            //     userUid,
            //     endDateStr,
            //     reportType,
            // );
            // timeseriesRespData = tsResp.Data;
            // getTimeseriesData 가 이미 신원과 함께 캐시에 넣으므로 중복 write 는 필요 없다
            timeSeriesRespData = await getTimeseriesData(userUid, endDateStr, reportType);
        } catch {
            isError = true;
        }

        if (timeSeriesRespData?.Evaluation) {
            try {
                // fetchMonthlyTimeseriesWithModulesForSingleMonth는 다른 날짜 호출 시 사용되므로
                // 새 API 사용 (useOnlyPublished = true) - 발행된 데이터만 조회
                // getModuleData 가 이미 신원과 함께 캐시에 넣으므로 중복 write 는 필요 없다
                moduleRespData = await getModuleData(userUid, endDateStr, version, reportType, true);
            } catch {
                isError = true;
            }
        } else {
            moduleData.value = null;
        }

        monthlyTimeseriesList.value.push({
            yearMonthKey: monthKey,
            timeseriesData: timeSeriesRespData,
            moduleData: moduleRespData,
            endDate: endDateStr,
            isError,
        });

        // 정렬 및 dedup 처리
        monthlyTimeseriesList.value = Array.from(
            new Map(monthlyTimeseriesList.value.map(item => [item.endDate, item])).values(),
        ).sort((a, b) => parseYMDToDate(a.endDate).getTime() - parseYMDToDate(b.endDate).getTime());

        // 현재 선택된 값 갱신
        currentSelectedEndDate.value = endDateStr;
    };

    const cleanupMonthlyMapCache = (baseEndDate: string) => {
        const base = parseYMDToDate(baseEndDate);
        const limitRange = 3;

        const keys = Object.keys(monthlyTimeseriesMap.value);
        keys.forEach(key => {
            const date = parseYMDToDate(key);
            const diffMonth = (date.getFullYear() - base.getFullYear()) * 12 + (date.getMonth() - base.getMonth());

            if (Math.abs(diffMonth) > limitRange) {
                delete monthlyTimeseriesMap.value[key];
                delete monthlyModuleMap.value[key];
            }
        });
    };

    const parseYMDToDate = (ymd: string): Date => {
        const [dateStr] = ymd.split('T');
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day); // month는 0부터 시작
    };

    /**
     * 중앙 업데이트 함수 (이전, 현재)
     * 관리자 페이지에서 리포트 진입 시 이전/현재 달 데이터를 미리 로드합니다.
     *
     * API 호출 전략:
     * - offset === 0 (baseEndDate, 첫 페이지): 기존 API 사용 (useOnlyPublished = false)
     *   → 테이블에서 클릭한 리포트의 정확한 데이터 조회
     * - offset === -1 (이전 달): 새 API 사용 (useOnlyPublished = true)
     *   → 클릭한 리포트 날짜가 아닌 다른 날짜이므로 발행된 데이터만 조회
     */
    // AIDEV-NOTE: seq 를 넘기지 않으면(월 이동 등) 진입 시점에 새 세대를 발급한다.
    // 행 클릭 경로는 beginReportSession() 이 발급한 seq 를 그대로 넘겨 같은 세대로 묶는다.
    const updateMonthlySurroundingData = async (
        userUid: string,
        baseEndDate: string,
        version: number,
        reportType: string,
        seq: number = ++requestSeq,
    ) => {
        if (!baseEndDate) return;

        // reportExistingEndDates가 비어있으면 baseEndDate만 호출
        // → 첫 페이지이므로 기존 API 사용 (useOnlyPublished = false)
        if (reportExistingEndDates.value.length === 0) {
            const tsData = await getTimeseriesData(userUid, baseEndDate, reportType, seq);
            if (isStale(seq)) return;
            if (tsData?.Evaluation) {
                await getModuleData(userUid, baseEndDate, version, reportType, false, seq);
                if (isStale(seq)) return;
            }
            currentSelectedEndDate.value = baseEndDate;
            clickedReportEndDate.value = baseEndDate; // 테이블에서 클릭한 리포트로 저장
            return;
        }

        const baseDate = parseYMDToDate(baseEndDate);
        const dateOffsets = [-1, 0]; // prev, current (이후 데이터는 로드하지 않음)

        for (const offset of dateOffsets) {
            // 주간 리포트는 baseEndDate로 넣은 일자만 호출
            if (reportType === 'Week' && offset !== 0) continue;

            // AIDEV-NOTE: Date.setMonth() 는 말일에서 오버플로한다.
            // 예) 3/31 에 setMonth(-1) → 2/31 → 3/3 이 되어 "이전 달"이 현재 달로 되돌아온다.
            // 그러면 offset -1 과 offset 0 이 같은 키를 쓰게 되고, 먼저 도는 offset -1 이
            // 발행본(useOnlyPublished=true)을 그 키에 채워버려서 offset 0 이 그걸 캐시 히트한다.
            // → 클릭한 행의 버전 대신 발행본 내용이 표시됐다. (3/31·5/31·7/31·10/31·12/31 해당)
            // 1일 기준으로 월을 옮긴 뒤 그 달의 실제 말일로 clamp 해서 오버플로를 없앤다.
            const target = new Date(baseDate.getFullYear(), baseDate.getMonth() + offset, 1);
            const lastDayOfTargetMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();

            const yyyy = target.getFullYear();
            const mm = String(target.getMonth() + 1).padStart(2, '0');
            // AIDEV-NOTE: '01' 이 아니라 clamp 한 baseDate 의 일자를 쓴다.
            // 아래에서 currentSelectedEndDate 를 baseEndDate(원본 EndDate)로 세팅하고
            // computedModuleData 가 그 값으로 맵을 조회하므로, '01' 로 바꾸면
            // write 키와 조회 키가 어긋나 화면이 비어버린다.
            const dd = String(Math.min(baseDate.getDate(), lastDayOfTargetMonth)).padStart(2, '0');
            const targetEndDate = `${yyyy}-${mm}-${dd}T00:00:00`;

            // offset === 0인 경우 (baseEndDate)
            // → 테이블에서 클릭한 리포트인지 확인하여 API 선택
            if (offset === 0) {
                const tsData = await getTimeseriesData(userUid, targetEndDate, reportType, seq);
                if (isStale(seq)) return;
                if (tsData?.Evaluation) {
                    // 테이블에서 클릭한 리포트인지 확인
                    // baseEndDate와 clickedReportEndDate를 월 단위로 비교하여 같은 리포트인지 확인
                    // 같은 리포트면 module-data API 사용 (useOnlyPublished = false)
                    // 다른 리포트면 module-data-only-published API 사용 (useOnlyPublished = true)
                    let isClickedReport = false;
                    if (clickedReportEndDate.value) {
                        const clickedDate = parseYMDToDate(clickedReportEndDate.value);
                        const baseDateForCompare = parseYMDToDate(baseEndDate);
                        // 년, 월만 비교 (같은 월의 리포트인지 확인)
                        isClickedReport =
                            clickedDate.getFullYear() === baseDateForCompare.getFullYear() &&
                            clickedDate.getMonth() === baseDateForCompare.getMonth();
                    } else {
                        // clickedReportEndDate가 null이면 이전/다음 버튼으로 이동한 것으로 간주
                        // (watchEffect에서 새 리포트 클릭 시 항상 설정되므로)
                        isClickedReport = false;
                    }
                    await getModuleData(userUid, targetEndDate, version, reportType, !isClickedReport, seq);
                    if (isStale(seq)) return;
                }
                continue;
            }

            // offset === -1일 때는 reportExistingEndDates의 범위 안에 있을 때만 호출
            // → 다른 날짜이므로 새 API 사용 (useOnlyPublished = true)
            const first = reportExistingEndDates.value.at(0);
            const isInRange = parseYMDToDate(targetEndDate) >= parseYMDToDate(first ?? '');

            if (!isInRange) continue;

            const tsData = await getTimeseriesData(userUid, targetEndDate, reportType, seq);
            if (isStale(seq)) return;
            if (tsData?.Evaluation) {
                await getModuleData(userUid, targetEndDate, version, reportType, true, seq);
                if (isStale(seq)) return;
            }
        }

        currentSelectedEndDate.value = baseEndDate;
        cleanupMonthlyMapCache(baseEndDate);
    };

    return {
        getTrendReportPage,
        updateCurrentPage,
        updateSelectedRowData,
        getTimeseriesData,
        getModuleData,
        getModuleDataOnlyPublished,
        // updateCurrentSelectedEvaluation,
        patchChangeIsPublish,
        patchChangeIsHumanConfirmed,
        patchChangeIsHaveToReview,
        patchModuleDataInSheet,
        postRegenerateModuleData,
        updateTargetSelectedRowData,
        removeItemFromList,
        initPageData,
        getTotalPage,
        getReportExistingEndDates,
        pageInfo,
        trendReportPageData,
        currentPage,
        selectedRowData,
        isLoadingPage,
        pageLoadError,
        currentSelectedEvaluation,
        timeseriesData,
        // moduleData,
        moduleDataMap,
        monthlyTimeseriesList,
        fetchMonthlyTimeseriesWithModules,
        currentSelectedEndDate,
        reportExistingEndDates,
        fetchMonthlyTimeseriesWithModulesForSingleMonth,
        computedTimeseriesData,
        computedModuleData,
        updateMonthlySurroundingData,
        monthlyModuleMap,
        monthlyTimeseriesMap,
        clickedReportEndDate,
        beginReportSession,
        isStale,
        isStaleSession,
    };
});

export default useProReportAdminStore;
